import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Text, YStack } from '@/components/ui';
import { Empty } from '@/components/ui/EmptyState';

import { GraphCanvas } from './GraphCanvas';
import { GraphDetailPanel } from './GraphDetailPanel';
import { GraphLegend } from './GraphLegend';
import { getWorldModelGraphQuery } from './world-model.query';

import type { GraphEntity, GraphSelection } from './world-model.types';

const ENTITY_TYPE_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
  '#84CC16',
  '#F97316',
];

function buildColorMap(entities: GraphEntity[]): Record<string, string> {
  const types = [...new Set(entities.map((e) => e.type))].sort();
  return Object.fromEntries(
    types.map((t, i) => [t, ENTITY_TYPE_COLORS[i % ENTITY_TYPE_COLORS.length]])
  );
}

export function GraphView() {
  const { data, isLoading, isError } = useQuery(getWorldModelGraphQuery);
  const [selection, setSelection] = useState<GraphSelection>(null);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [hiddenSemantics, setHiddenSemantics] = useState<Set<string>>(
    new Set()
  );
  const [searchQuery, setSearchQuery] = useState('');

  const toggleType = (type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const toggleSemantic = (semantic: string) => {
    setHiddenSemantics((prev) => {
      const next = new Set(prev);
      if (next.has(semantic)) next.delete(semantic);
      else next.add(semantic);
      return next;
    });
  };

  const entities = useMemo(() => data?.entities ?? [], [data]);
  const relationships = useMemo(() => data?.relationships ?? [], [data]);
  const colorMap = useMemo(() => buildColorMap(entities), [entities]);

  const semanticTypes = useMemo(() => {
    const types = new Set<string>();
    for (const rel of relationships) {
      if (rel.semantic) types.add(rel.semantic);
    }
    return [...types].sort();
  }, [relationships]);

  const matchedEntityIds = useMemo<Set<string> | null>(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return null;
    const matched = new Set<string>();
    for (const entity of entities) {
      const nameHit = (entity.name ?? '').toLowerCase().includes(term);
      const contentHit = (entity.content ?? '').toLowerCase().includes(term);
      const propHit = JSON.stringify(entity.properties ?? {})
        .toLowerCase()
        .includes(term);
      if (nameHit || contentHit || propHit) matched.add(entity.id);
    }
    return matched;
  }, [entities, searchQuery]);

  // Memoized so selecting a node (re-render) doesn't produce new array refs
  // and inadvertently reinitialize the canvas.
  const visibleEntities = useMemo(() => {
    const typeFiltered = entities.filter((e) => !hiddenTypes.has(e.type));
    if (!matchedEntityIds) return typeFiltered;
    const neighborIds = new Set<string>();
    for (const rel of relationships) {
      if (matchedEntityIds.has(rel.source)) neighborIds.add(rel.target);
      if (matchedEntityIds.has(rel.target)) neighborIds.add(rel.source);
    }
    return typeFiltered.filter(
      (e) => matchedEntityIds.has(e.id) || neighborIds.has(e.id)
    );
  }, [entities, hiddenTypes, matchedEntityIds, relationships]);

  const visibleRelationships = useMemo(() => {
    const visibleIds = new Set(visibleEntities.map((e) => e.id));
    return relationships.filter(
      (r) =>
        visibleIds.has(r.source) &&
        visibleIds.has(r.target) &&
        (!r.semantic || !hiddenSemantics.has(r.semantic))
    );
  }, [relationships, visibleEntities, hiddenSemantics]);

  if (isLoading) {
    return (
      <YStack sizeFull center className="bg-surface-subtle">
        <div className="w-6 h-6 rounded-full border-2 border-foreground-muted border-t-transparent animate-spin" />
      </YStack>
    );
  }

  if (isError) {
    return (
      <YStack sizeFull center className="bg-surface-subtle">
        <Text size="sm" lowContrast>
          Failed to load graph data
        </Text>
      </YStack>
    );
  }

  if (entities.length === 0) {
    return (
      <YStack sizeFull className="justify-center bg-surface-subtle">
        <Empty.Root>
          <Empty.Media variant="icon">
            <Icons.memory />
          </Empty.Media>
          <Empty.Header>
            <Empty.Title>No graph data yet</Empty.Title>
            <Empty.Description>
              Graph data appears after memory extraction runs.
            </Empty.Description>
          </Empty.Header>
        </Empty.Root>
      </YStack>
    );
  }

  return (
    // overflow-hidden scoped to the canvas layer only — overlays are not clipped
    <div className="relative w-full h-full bg-surface-subtle">
      <div className="absolute inset-0 overflow-hidden">
        <GraphCanvas
          entities={visibleEntities}
          relationships={visibleRelationships}
          colorMap={colorMap}
          matchedIds={matchedEntityIds}
          onSelect={setSelection}
        />
      </div>

      <GraphLegend
        colorMap={colorMap}
        hiddenTypes={hiddenTypes}
        onToggleType={toggleType}
        onSelectAllTypes={() => setHiddenTypes(new Set())}
        onDeselectAllTypes={() =>
          setHiddenTypes(new Set(Object.keys(colorMap)))
        }
        semanticTypes={semanticTypes}
        hiddenSemantics={hiddenSemantics}
        onToggleSemantic={toggleSemantic}
        onSelectAllSemantics={() => setHiddenSemantics(new Set())}
        onDeselectAllSemantics={() =>
          setHiddenSemantics(new Set(semanticTypes))
        }
        searchQuery={searchQuery}
        matchCount={matchedEntityIds?.size ?? null}
        onSearchChange={setSearchQuery}
      />

      {/* Detail popup — bottom-right, floats above the graph */}
      <GraphDetailPanel
        selection={selection}
        onClose={() => setSelection(null)}
      />
    </div>
  );
}
